// Viewport math used by Canvas, ZoomControls, Minimap. Kept pure so the
// zoom-anchor and fit-to-content geometry can be tested without React.

export interface Viewport {
  x: number;
  y: number;
  scale: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface BBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const MIN_SCALE = 0.1;
export const MAX_SCALE = 8;
export const ZOOM_STEP = 1.2;

export function clampScale(scale: number): number {
  return Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));
}

// Zoom keeping `anchor` (in stage/screen coords) over the same world point.
// Used by both the wheel handler and the +/- buttons (which anchor on the
// container center).
export function zoomAt(viewport: Viewport, newScale: number, anchor: { x: number; y: number }): Viewport {
  const scale = clampScale(newScale);
  const worldX = (anchor.x - viewport.x) / viewport.scale;
  const worldY = (anchor.y - viewport.y) / viewport.scale;
  return {
    scale,
    x: anchor.x - worldX * scale,
    y: anchor.y - worldY * scale,
  };
}

// Reset to 100% centered on the same world point that's currently centered.
export function resetZoom(viewport: Viewport, size: Size): Viewport {
  return zoomAt(viewport, 1, { x: size.width / 2, y: size.height / 2 });
}

// Smallest enclosing bbox of a set of shape bboxes. Returns null if empty.
export function unionBBox(bboxes: readonly BBox[]): BBox | null {
  if (bboxes.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const b of bboxes) {
    if (b.x < minX) minX = b.x;
    if (b.y < minY) minY = b.y;
    if (b.x + b.w > maxX) maxX = b.x + b.w;
    if (b.y + b.h > maxY) maxY = b.y + b.h;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

// Fit a world-space bbox into the container with `margin` px of padding on
// each side. Returns the new viewport. Margin is in screen-space (post-scale).
export function fitBBoxToContainer(bbox: BBox, container: Size, margin = 64): Viewport {
  const safeW = Math.max(1, container.width - margin * 2);
  const safeH = Math.max(1, container.height - margin * 2);
  const scaleX = safeW / Math.max(1, bbox.w);
  const scaleY = safeH / Math.max(1, bbox.h);
  const scale = clampScale(Math.min(scaleX, scaleY));
  const bboxCenterX = bbox.x + bbox.w / 2;
  const bboxCenterY = bbox.y + bbox.h / 2;
  return {
    scale,
    x: container.width / 2 - bboxCenterX * scale,
    y: container.height / 2 - bboxCenterY * scale,
  };
}
